import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';

import * as graphql from 'generated/graphql';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import DeleteButton from 'packages/kakapo-ui-kit/components/editors/DeleteButton';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';

const Row = styled(Flex)`
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
	align-items: center;
`;

const Icon = styled(Box)`
	flex-grow: 0;
	flex-shrink: 0;
	width: 34px;
	height: 34px;
	border-radius: 4px;
	background: ${(props) => props.theme.colors.slateBlue};
	color: white;
	text-align: center;
	font-size: 13px;
	line-height: 34px;
`;

const Link = styled.a`
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 16px;
	text-decoration: underline;
	flex-grow: 0;
	flex-shrink: 1;
	word-break: break-all;
`;

const Name = styled.a`
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 16px;
	flex-grow: 0;
	flex-shrink: 1;
	word-break: break-all;
`;

const Status = styled(Box)`
	flex-grow: 0;
	flex-shrink: 0;
	color: ${(props) => props.theme.colors.lightBlueDark};
`;

function UserDownload(props: { user: graphql.User; download: graphql.UserDownload }) {
	const { t } = useTranslation();

	const ext = props.download.name.split('.')[1];

	const [commit, { loading }] = graphql.useDeleteUserDownloadMutation({
		variables: { input: { id: props.download.id } },
	});

	return (
		<Row py={12} px={12}>
			<Icon mr={3}>{ext ? `.${ext}` : '?'}</Icon>
			{props.download.downloadLink ? (
				<Link href={props.download.downloadLink} download target="_blank">
					{props.download.name}
				</Link>
			) : (
				<Name>{props.download.name}</Name>
			)}
			<Box flexGrow={1} />
			<Status ml={3}>
				{props.download.status === graphql.UserDownloadStatus.Pending &&
					t('user.downloads.status.pending')}
				{props.download.status === graphql.UserDownloadStatus.Failed &&
					t(`user.downloads.status.failed.${props.download.failedReason}`)}
				{props.download.status === graphql.UserDownloadStatus.Running &&
					t('user.downloads.status.running', {
						progress: (props.download.progress * 100).toFixed(2),
					})}
				{props.download.status === graphql.UserDownloadStatus.Success &&
					t('user.downloads.status.success')}
			</Status>
			{props.download.viewerCanDelete && (
				<Flex ml={3} width={28} justifyContent="center" flexShrink={0} flexGrow={0}>
					{loading ? (
						<Spinner isLoading size="18px" verticalMargin={0} />
					) : (
						<DeleteButton onClick={() => commit()} />
					)}
				</Flex>
			)}
		</Row>
	);
}

export default function UserDownloads(props: { user: graphql.User }) {
	const { t } = useTranslation();

	const query = graphql.useUserDownloadsQuery({ variables: { id: props.user.id } });

	return (
		<>
			<Flex alignItems="center">
				<SectionTitle>{t('user.downloads.title')}</SectionTitle>
			</Flex>
			<Column width={1 / 2}>
				{query.data && query.data.user.downloads.length > 0 ? (
					query.data.user.downloads.map((doc) => (
						<UserDownload key={doc.id} user={props.user} download={doc as graphql.UserDownload} />
					))
				) : query.loading ? (
					<Spinner isLoading size="18px" />
				) : (
					<Box textAlign="center" mt={3}>
						<Paragraph color="slateBlue">{t('user.downloads.empty')}</Paragraph>
					</Box>
				)}
			</Column>
		</>
	);
}
